/* Sprint C+D — Social Evidence Intake Panel (Sprint D).
 *
 * Lets the operator submit URL / caption / metric / screenshot ref /
 * export evidence into the project's evidence_records pool. EVERY
 * artifact lands PENDING and must be approved via the existing
 * Evidence panel before downstream agents (Business Plan / Concepts /
 * Script) can consume it.
 *
 * Hard-NO: scraping, headless browsers, OCR, follower auto-fetch.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Link2, Quote, Hash, Image as ImageIcon, FileText, RefreshCw,
} from "lucide-react";
import SocialHonestyBanner from "@/components/SocialHonestyBanner";

const INTAKE_KINDS = [
    { id: "url",        label: "Profile URL", icon: Link2 },
    { id: "caption",    label: "Caption text", icon: Quote },
    { id: "metric",     label: "Follower / metric", icon: Hash },
    { id: "screenshot", label: "Uploaded screenshot", icon: ImageIcon },
    { id: "export",     label: "Pasted export text", icon: FileText },
];

export default function SocialEvidenceIntakePanel({ projectId }) {
    const [kind, setKind] = useState("url");
    const [form, setForm] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [history, setHistory] = useState(null);

    const loadHistory = useCallback(async () => {
        if (!projectId) return;
        try {
            const { data } = await api.get(
                `/research-intel/projects/${projectId}/social/intake/history?limit=50`,
            );
            setHistory(data);
        } catch (e) {
            // best-effort
        }
    }, [projectId]);

    useEffect(() => { loadHistory(); }, [loadHistory]);

    const reset = () => setForm({});

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = useCallback(async () => {
        if (!projectId) return;
        setSubmitting(true);
        try {
            const body = {
                intake_kind: kind,
                ...form,
            };
            // Convert metric_value to number if numeric.
            if (kind === "metric" && body.metric_value != null
                && !Number.isNaN(Number(body.metric_value))) {
                body.metric_value = Number(body.metric_value);
            }
            const { data } = await api.post(
                `/research-intel/projects/${projectId}/social/intake`,
                body,
            );
            toast.success(
                `Submitted as PENDING. Approve via Research Evidence panel.`,
            );
            reset();
            loadHistory();
            return data;
        } catch (e) {
            toast.error(
                e?.userMessage
                || e?.response?.data?.detail?.message
                || e?.response?.data?.detail
                || "Submission failed.",
            );
        } finally {
            setSubmitting(false);
        }
    }, [projectId, kind, form, loadHistory]);

    return (
        <div
            className="mt-4 card-base p-4 space-y-3"
            data-testid="social-evidence-intake-panel"
        >
            <div className="text-sm font-semibold text-zinc-100">
                Social Evidence Intake (Sprint D)
            </div>

            <SocialHonestyBanner />

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
                {INTAKE_KINDS.map(({ id, label, icon: Icon }) => (
                    <button
                        key={id}
                        onClick={() => { setKind(id); reset(); }}
                        className={
                            "flex items-center gap-1.5 px-2 py-1.5 rounded-md border text-[11px] " +
                            (kind === id
                                ? "border-violet-500/40 bg-violet-500/10 text-violet-200"
                                : "border-zinc-800 bg-zinc-950/40 text-zinc-400 hover:text-zinc-200")
                        }
                        data-testid={`social-intake-kind-${id}`}
                    >
                        <Icon className="w-3.5 h-3.5" />
                        {label}
                    </button>
                ))}
            </div>

            <div className="space-y-2 pt-1">
                {kind === "url" && (
                    <>
                        <FormRow label="Profile URL" hint="Instagram / Facebook / LinkedIn / YouTube / X / TikTok URL — Furmais will NOT visit it">
                            <input
                                value={form.url || ""}
                                onChange={(e) => set("url", e.target.value)}
                                placeholder="https://instagram.com/yourbrand"
                                className="w-full bg-zinc-900 border border-zinc-800 rounded-md p-1.5 text-xs"
                                data-testid="social-intake-url-input"
                            />
                        </FormRow>
                    </>
                )}
                {kind === "caption" && (
                    <>
                        <FormRow label="Source URL (optional)">
                            <input
                                value={form.url || ""}
                                onChange={(e) => set("url", e.target.value)}
                                className="w-full bg-zinc-900 border border-zinc-800 rounded-md p-1.5 text-xs"
                                data-testid="social-intake-caption-url"
                            />
                        </FormRow>
                        <FormRow label="Caption text">
                            <textarea
                                rows={3}
                                value={form.caption_text || ""}
                                onChange={(e) => set("caption_text", e.target.value)}
                                placeholder="Paste the caption text you saw on the post"
                                className="w-full bg-zinc-900 border border-zinc-800 rounded-md p-1.5 text-xs"
                                data-testid="social-intake-caption-input"
                            />
                        </FormRow>
                    </>
                )}
                {kind === "metric" && (
                    <>
                        <FormRow label="Profile URL (optional)">
                            <input
                                value={form.url || ""}
                                onChange={(e) => set("url", e.target.value)}
                                className="w-full bg-zinc-900 border border-zinc-800 rounded-md p-1.5 text-xs"
                                data-testid="social-intake-metric-url"
                            />
                        </FormRow>
                        <div className="grid grid-cols-2 gap-2">
                            <FormRow label="Metric name">
                                <input
                                    value={form.metric_name || ""}
                                    onChange={(e) => set("metric_name", e.target.value)}
                                    placeholder="followers / likes / views"
                                    className="w-full bg-zinc-900 border border-zinc-800 rounded-md p-1.5 text-xs"
                                    data-testid="social-intake-metric-name"
                                />
                            </FormRow>
                            <FormRow label="Value (as you saw it)">
                                <input
                                    value={form.metric_value || ""}
                                    onChange={(e) => set("metric_value", e.target.value)}
                                    placeholder="12500"
                                    className="w-full bg-zinc-900 border border-zinc-800 rounded-md p-1.5 text-xs"
                                    data-testid="social-intake-metric-value"
                                />
                            </FormRow>
                        </div>
                    </>
                )}
                {kind === "screenshot" && (
                    <>
                        <FormRow label="Profile URL (optional)">
                            <input
                                value={form.url || ""}
                                onChange={(e) => set("url", e.target.value)}
                                className="w-full bg-zinc-900 border border-zinc-800 rounded-md p-1.5 text-xs"
                                data-testid="social-intake-screenshot-url"
                            />
                        </FormRow>
                        <FormRow label="Screenshot asset id" hint="Upload the image via the existing /api/uploads route first; paste its asset id here">
                            <input
                                value={form.screenshot_asset_id || ""}
                                onChange={(e) => set("screenshot_asset_id", e.target.value)}
                                placeholder="asset-id-from-uploads"
                                className="w-full bg-zinc-900 border border-zinc-800 rounded-md p-1.5 text-xs"
                                data-testid="social-intake-screenshot-asset-id"
                            />
                        </FormRow>
                    </>
                )}
                {kind === "export" && (
                    <>
                        <FormRow label="Profile URL (optional)">
                            <input
                                value={form.url || ""}
                                onChange={(e) => set("url", e.target.value)}
                                className="w-full bg-zinc-900 border border-zinc-800 rounded-md p-1.5 text-xs"
                                data-testid="social-intake-export-url"
                            />
                        </FormRow>
                        <FormRow label="Pasted export text">
                            <textarea
                                rows={4}
                                value={form.export_text || ""}
                                onChange={(e) => set("export_text", e.target.value)}
                                placeholder="Paste relevant rows from the platform's official export"
                                className="w-full bg-zinc-900 border border-zinc-800 rounded-md p-1.5 text-xs"
                                data-testid="social-intake-export-input"
                            />
                        </FormRow>
                    </>
                )}

                <button
                    onClick={submit}
                    disabled={submitting}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs"
                    data-testid="social-intake-submit-btn"
                >
                    {submitting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : null}
                    Submit as PENDING
                </button>
            </div>

            {history?.items?.length ? (
                <div className="pt-3 border-t border-zinc-800 space-y-1.5">
                    <div className="text-[11px] uppercase tracking-wide text-zinc-500">
                        Recent submissions (audit)
                    </div>
                    <div className="space-y-1" data-testid="social-intake-history">
                        {history.items.slice(0, 8).map((r) => (
                            <div
                                key={r.id}
                                className="rounded-md border border-zinc-800 bg-zinc-950/40 px-2 py-1 text-[11px] flex items-center justify-between gap-2"
                            >
                                <div className="truncate text-zinc-300">
                                    <span className="text-zinc-500">{r.source_kind}</span>
                                    {" · "}
                                    <span>{r.predicate}</span>
                                </div>
                                <span className="px-1.5 py-0.5 rounded-sm border border-amber-500/40 text-amber-300 bg-amber-500/5 text-[10px]">
                                    {r.approval_state}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function FormRow({ label, hint, children }) {
    return (
        <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
            {children}
            {hint ? <div className="text-[10px] text-zinc-500">{hint}</div> : null}
        </div>
    );
}
